import { lazy, Suspense, useEffect, useRef } from "react";
import type { FocusEvent as ReactFocusEvent, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import type { FpsPointerTarget, FpsPointerRef } from "./FpsAkCanvas";

const FpsAkCanvas = lazy(() => import("./FpsAkCanvas"));

interface GamesGridProps {
  onPlayPong: () => void;
  onOpenFPSModal: () => void;
  onPlayTowerDefense: () => void;
  onPlaySnake: () => void;
  onPlayMath: () => void;
  onOpenTournaments: () => void;
}

type CardItem = {
  id: string;
  title: string;
  desc: string;
  stats: { label: string; value: string; muted?: boolean }[];
  onClick?: () => void;
  art: ReactNode;
};

function HeaderDot({ live = false }: { live?: boolean }) {
  return (
    <span
      className="w-1.5 h-1.5 rounded-full"
      style={{
        background: live ? "var(--color-live)" : "var(--color-brand)",
        boxShadow: live ? "0 0 8px var(--color-live)" : "0 0 10px var(--color-brand)",
      }}
    />
  );
}

function GamesGridMotionStyles() {
  return (
    <style>{`
      @keyframes hupper-card-outline {
        0% { stroke-dashoffset: 100; opacity: .25; }
        12% { opacity: .9; }
        100% { stroke-dashoffset: 0; opacity: 1; }
      }
      @keyframes hupper-card-glow {
        0%,100% { opacity: 0; }
        18%,100% { opacity: 1; }
      }
      .hupper-game-card {
        isolation: isolate;
      }
      .hupper-game-card:hover {
        box-shadow: 0 18px 44px rgba(0, 0, 0, 0.34), 0 0 0 1px rgba(245, 77, 18, 0.08);
      }
      .hupper-fps-showcase {
        --fps-tilt-x: 0deg;
        --fps-tilt-y: 0deg;
        --fps-glow-x: 54%;
        --fps-glow-y: 34%;
        transform: perspective(1200px) rotateX(var(--fps-tilt-x)) rotateY(var(--fps-tilt-y)) translateY(0);
        transform-style: preserve-3d;
        will-change: transform;
        background:
          radial-gradient(circle at var(--fps-glow-x) var(--fps-glow-y), rgba(245, 77, 18, 0.28), transparent 32%),
          linear-gradient(145deg, #17110e 0%, #080706 52%, #030303 100%);
        border: 1px solid rgba(245, 77, 18, 0.2);
        box-shadow: 0 18px 46px rgba(0, 0, 0, 0.38);
      }
      .hupper-game-card.hupper-fps-showcase:hover,
      .hupper-game-card.hupper-fps-showcase:focus-visible {
        transform: perspective(1200px) rotateX(var(--fps-tilt-x)) rotateY(var(--fps-tilt-y)) translateY(-4px);
        box-shadow: 0 26px 68px rgba(0, 0, 0, 0.48), 0 0 36px rgba(245, 77, 18, 0.12);
      }
      .hupper-fps-showcase::before {
        content: "";
        position: absolute;
        inset: 0;
        z-index: 40;
        border-radius: inherit;
        padding: 1px;
        pointer-events: none;
        background:
          linear-gradient(125deg, rgba(255, 132, 82, 0.92), rgba(245, 77, 18, 0.2) 38%, rgba(255, 255, 255, 0.12) 58%, rgba(245, 77, 18, 0.74));
        -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
        -webkit-mask-composite: xor;
        mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
        mask-composite: exclude;
        opacity: 0.52;
        transition: opacity 0.22s ease;
      }
      .hupper-fps-showcase:hover::before,
      .hupper-fps-showcase:focus-visible::before {
        opacity: 0.95;
      }
      .hupper-fps-showcase::after {
        content: "";
        position: absolute;
        inset: 0;
        z-index: 1;
        pointer-events: none;
        background:
          radial-gradient(circle at var(--fps-glow-x) var(--fps-glow-y), rgba(255, 94, 31, 0.18), transparent 34%),
          linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.08) 46%, rgba(0, 0, 0, 0.72) 100%);
        transition: opacity 0.2s ease;
      }
      .hupper-fps-grid {
        background-image:
          linear-gradient(rgba(255,255,255,0.055) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.055) 1px, transparent 1px);
        background-size: 42px 42px;
        mask-image: linear-gradient(180deg, transparent 0%, #000 24%, #000 68%, transparent 100%);
        -webkit-mask-image: linear-gradient(180deg, transparent 0%, #000 24%, #000 68%, transparent 100%);
        transform: perspective(760px) rotateX(62deg) translateY(34%) scale(1.35);
        transform-origin: center bottom;
      }
      .hupper-fps-scanlines {
        background: repeating-linear-gradient(0deg, rgba(255,255,255,0.028) 0 1px, transparent 1px 4px);
        mix-blend-mode: overlay;
      }
      .hupper-fps-canvas-shell {
        transform: translateZ(44px);
      }
      .hupper-fps-content {
        transform: translateZ(62px);
      }
      @media (prefers-reduced-motion: reduce) {
        .hupper-fps-showcase,
        .hupper-game-card.hupper-fps-showcase:hover,
        .hupper-game-card.hupper-fps-showcase:focus-visible {
          transform: none;
          transition: none;
        }
        .hupper-fps-canvas-shell,
        .hupper-fps-content {
          transform: none;
        }
      }
      .hupper-hover-outline {
        opacity: 0;
        transition: opacity 0.18s ease;
        inset: -1px;
      }
      .hupper-game-card:hover .hupper-hover-outline {
        opacity: 1;
      }
      .hupper-hover-outline svg {
        width: 100%;
        height: 100%;
        overflow: visible;
        filter: drop-shadow(0 0 10px rgba(245, 77, 18, 0.22));
      }
      .hupper-hover-outline rect {
        fill: none;
        stroke: var(--color-brand);
        stroke-width: 1.8;
        vector-effect: non-scaling-stroke;
        stroke-dasharray: 100;
        stroke-dashoffset: 100;
      }
      .hupper-game-card:hover .hupper-hover-outline rect {
        animation: hupper-card-outline 1.24s cubic-bezier(0.22, 1, 0.36, 1) forwards;
      }
      .hupper-hover-glow {
        opacity: 0;
        transition: opacity 0.22s ease;
      }
      .hupper-game-card:hover .hupper-hover-glow {
        animation: hupper-card-glow 1.24s ease forwards;
      }
      @keyframes hupper-pong-left {
        0%,100% { transform: translateY(-50%) translateY(-30px); }
        50% { transform: translateY(-50%) translateY(30px); }
      }
      @keyframes hupper-pong-right {
        0%,100% { transform: translateY(-50%) translateY(30px); }
        50% { transform: translateY(-50%) translateY(-30px); }
      }
      @keyframes hupper-pong-ball {
        0% { transform: translate(-50%,-50%) translateX(-90px); }
        50% { transform: translate(-50%,-50%) translateX(90px) translateY(20px); }
        100% { transform: translate(-50%,-50%) translateX(-90px); }
      }
      @keyframes hupper-creep-a {
        0%,100% { transform: scale(1) translateX(0); opacity: .82; }
        50% { transform: scale(1.08) translateX(2px); opacity: 1; }
      }
      @keyframes hupper-creep-b {
        0%,100% { transform: scale(1) translateY(0); opacity: .78; }
        50% { transform: scale(1.08) translateY(-2px); opacity: 1; }
      }
      @keyframes hupper-orb-pulse {
        0%,100% { transform: translate(-50%,-30%) scale(1); opacity: .7; }
        50% { transform: translate(-50%,-30%) scale(1.25); opacity: 1; }
      }
      @keyframes hupper-snake-slither {
        to { stroke-dashoffset: -40; }
      }
      @keyframes hupper-snake-tongue {
        0%,100% { opacity: 0; transform: scaleX(.6); }
        20%,80% { opacity: 1; transform: scaleX(1); }
      }
      @keyframes hupper-snake-fruit {
        0%,100% { transform: scale(1); }
        50% { transform: scale(1.18); }
      }
    `}</style>
  );
}

function CardHoverOutline() {
  return (
    <>
      <div
        className="hupper-hover-glow absolute inset-0 rounded-[inherit] pointer-events-none"
        style={{
          boxShadow: "0 0 24px rgba(245,77,18,0.12)",
        }}
      />
      <div className="hupper-hover-outline absolute inset-0 rounded-[inherit] pointer-events-none z-[30]">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <rect x="1" y="1" width="98" height="98" rx="14" ry="14" pathLength="100" />
        </svg>
      </div>
    </>
  );
}


function FpsArenaShowcase({ item }: { item: CardItem }) {
  const pointerRef = useRef<FpsPointerTarget>({
    active: 0,
    reduceMotion: false,
    x: 0,
    y: 0,
  });

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncPreference = () => {
      pointerRef.current.reduceMotion = media.matches;
    };

    syncPreference();
    media.addEventListener("change", syncPreference);
    return () => media.removeEventListener("change", syncPreference);
  }, []);

  const setCardPointer = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const card = event.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width - 0.5) * 2));
    const y = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height - 0.5) * 2));

    pointerRef.current.x = x;
    pointerRef.current.y = y;
    pointerRef.current.active = 1;

    if (pointerRef.current.reduceMotion) return;

    card.style.setProperty("--fps-tilt-x", `${(-y * 3).toFixed(2)}deg`);
    card.style.setProperty("--fps-tilt-y", `${(x * 4).toFixed(2)}deg`);
    card.style.setProperty("--fps-glow-x", `${((x + 1) * 50).toFixed(1)}%`);
    card.style.setProperty("--fps-glow-y", `${((y + 1) * 50).toFixed(1)}%`);
  };

  const resetCardPointer = (card: HTMLButtonElement) => {
    pointerRef.current.x = 0;
    pointerRef.current.y = 0;
    pointerRef.current.active = 0;
    card.style.setProperty("--fps-tilt-x", "0deg");
    card.style.setProperty("--fps-tilt-y", "0deg");
    card.style.setProperty("--fps-glow-x", "54%");
    card.style.setProperty("--fps-glow-y", "34%");
  };

  const handlePointerLeave = (event: ReactPointerEvent<HTMLButtonElement>) => {
    resetCardPointer(event.currentTarget);
  };

  const handleBlur = (event: ReactFocusEvent<HTMLButtonElement>) => {
    resetCardPointer(event.currentTarget);
  };

  return (
    <button
      type="button"
      onClick={item.onClick}
      onPointerMove={setCardPointer}
      onPointerLeave={handlePointerLeave}
      onBlur={handleBlur}
      aria-label="Open FPS Arena"
      className="hupper-game-card hupper-fps-showcase group relative min-h-[520px] md:min-h-[538px] md:col-span-2 md:row-span-2 rounded-[14px] overflow-hidden text-left transition-transform duration-200"
      style={{ cursor: "pointer" }}
    >
      <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.08),transparent_30%)]" />
      <div className="hupper-fps-grid absolute inset-x-[-12%] bottom-[-18%] h-[62%] z-[1] pointer-events-none" />
      <div className="hupper-fps-scanlines absolute inset-0 z-[2] opacity-45 pointer-events-none" />

      <div className="absolute left-5 right-5 top-5 z-[5] flex flex-wrap items-center justify-between gap-3 hupper-fps-content">
        <span className="font-mono inline-flex items-center gap-2 rounded-full border border-[rgba(245,77,18,0.36)] bg-black/45 px-3 py-1.5 text-[10px] tracking-[0.16em] uppercase text-white backdrop-blur-md">
          <span className="h-[5px] w-[5px] rounded-full bg-[var(--color-brand)] shadow-[0_0_10px_var(--color-brand)]" />
          Live Queue
        </span>
        <span className="font-mono rounded-full border border-white/10 bg-white/[0.045] px-3 py-1.5 text-[10px] tracking-[0.16em] uppercase text-[var(--color-text-tertiary)] backdrop-blur-md">
          Tactical 1V1
        </span>
      </div>

      <div className="hupper-fps-canvas-shell absolute inset-x-0 top-[34px] bottom-[132px] md:bottom-[142px] z-[4] pointer-events-none">
        <Suspense fallback={null}>
          <FpsAkCanvas pointerRef={pointerRef} />
        </Suspense>
      </div>

      <div className="hupper-fps-content absolute inset-x-5 bottom-5 z-[6]">
        <div className="max-w-[620px]">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--color-brand)]">
            Hupper Combat
          </div>
          <h3 className="mt-2 mb-0 text-[36px] md:text-[46px] leading-none font-bold tracking-[-0.035em] text-[var(--color-text-primary)]">
            FPS Arena
          </h3>
          <p className="mt-3 mb-0 max-w-[540px] text-[14px] md:text-[15px] leading-[1.5] text-[var(--color-text-secondary)]">
            Precision duels, clean mechanics and fast rounds built for skill-based stakes.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {["Buy Phase", "Skill Duel", "Live Queue"].map((chip) => (
              <span
                key={chip}
                className="font-mono rounded-full border border-white/10 bg-white/[0.055] px-3 py-1.5 text-[10px] tracking-[0.14em] uppercase text-[var(--color-text-secondary)]"
              >
                {chip}
              </span>
            ))}
          </div>
        </div>
      </div>
    </button>
  );
}


function TowerDefenseArt() {
  const cells = [
    "........",
    "..T###..",
    "...T#...",
    ".###..T.",
    "....T...",
  ];

  return (
    <div className="absolute inset-0 p-[18px]">
      <div className="grid grid-cols-8 grid-rows-5 gap-[3px] h-full">
        {cells.join("").split("").map((cell, index) => {
          const isPath = cell === "#";
          const isTower = cell === "T";
          const isCreep = index === 14 || index === 27;
          return (
            <div
              key={index}
              className="relative rounded-[2px]"
              style={{ background: isPath ? "#1f1a16" : "#161616" }}
            >
              {isCreep && (
                <span
                  className="absolute inset-[25%] rounded-full"
                  style={{
                    background: "#fff",
                    boxShadow: "0 0 8px rgba(255,255,255,0.6)",
                    animation: index === 14 ? "hupper-creep-a 1.6s ease-in-out infinite" : "hupper-creep-b 1.8s ease-in-out infinite",
                  }}
                />
              )}
              {isTower && (
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[58%] w-[18px] h-[24px]">
                  <div className="absolute left-1/2 bottom-0 -translate-x-1/2 w-[18px] h-[5px] rounded-[2px]" style={{ background: "#3a2618" }} />
                  <div
                    className="absolute left-1/2 bottom-1 -translate-x-1/2 w-[11px] h-[13px] rounded-t-[2px]"
                    style={{
                      background: "linear-gradient(180deg,#ff7a44 0%,#ff5e1f 60%,#c83d09 100%)",
                      boxShadow: "0 0 10px rgba(255,94,31,0.55)",
                    }}
                  />
                  <div
                    className="absolute left-1/2 bottom-[15px] -translate-x-1/2 w-[13px] h-[3px]"
                    style={{
                      background: "#1a0a02",
                      clipPath:
                        "polygon(0 0,100% 0,100% 100%,86% 100%,86% 40%,71% 40%,71% 100%,57% 100%,57% 40%,43% 40%,43% 100%,29% 100%,29% 40%,14% 40%,14% 100%,0 100%)",
                    }}
                  />
                  <div
                    className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-[30%] w-[5px] h-[5px] rounded-full"
                    style={{
                      background: "#fff",
                      boxShadow: "0 0 8px #fff, 0 0 14px var(--color-brand)",
                      animation: "hupper-orb-pulse 1.6s ease-in-out infinite",
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MathArt() {
  return (
    <div className="absolute inset-0 grid place-items-center px-5">
      <div className="font-mono text-[34px] text-[var(--color-text-primary)] tracking-[-0.01em] flex items-center gap-3.5">
        <span>147</span>
        <span className="text-[var(--color-brand)]">×</span>
        <span>23</span>
        <span className="text-[var(--color-brand)]">=</span>
        <span className="inline-block w-[18px] h-[32px] rounded-[3px] border border-[var(--color-brand)] bg-[rgba(255,94,31,0.08)] animate-pulse" />
      </div>
      <div className="absolute left-[18px] right-[18px] bottom-[14px] flex gap-[5px] flex-wrap">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "↵"].map((key) => (
          <span
            key={key}
            className="font-mono text-[9px] px-[6px] py-[3px] rounded-[4px]"
            style={{ color: "var(--color-text-tertiary)", border: "1px solid var(--color-line-emphasis)" }}
          >
            {key}
          </span>
        ))}
      </div>
    </div>
  );
}

function PongArt() {
  return (
    <div className="absolute inset-0 p-[30px]">
      <div
        className="absolute left-[30px] top-1/2 -translate-y-1/2 w-[3px] h-12 rounded-full bg-[var(--color-brand)]"
        style={{ boxShadow: "0 0 8px rgba(255,94,31,0.5)", animation: "hupper-pong-left 3.2s ease-in-out infinite" }}
      />
      <div
        className="absolute right-[30px] top-1/2 -translate-y-1/2 w-[3px] h-12 rounded-full bg-[var(--color-text-primary)]"
        style={{ animation: "hupper-pong-right 3.2s ease-in-out infinite" }}
      />
      <div
        className="absolute left-1/2 top-1/2 w-[6px] h-[6px] rounded-[1px] bg-[var(--color-text-primary)] -translate-x-1/2 -translate-y-1/2"
        style={{ animation: "hupper-pong-ball 3.2s linear infinite" }}
      />
    </div>
  );
}

function SnakeArt() {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.025) 1px,transparent 1px), linear-gradient(90deg,rgba(255,255,255,.025) 1px,transparent 1px)",
          backgroundSize: "18px 18px",
          maskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, #000 30%, transparent 90%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, #000 30%, transparent 90%)",
        }}
      />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 320 220" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <defs>
          <linearGradient id="snake-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ff7a44" />
            <stop offset="60%" stopColor="#ff5e1f" />
            <stop offset="100%" stopColor="#c83d09" />
          </linearGradient>
          <radialGradient id="fruit-grad" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#fff" />
            <stop offset="60%" stopColor="#fef3c7" />
            <stop offset="100%" stopColor="#fbbf24" />
          </radialGradient>
        </defs>
        <path d="M40 180 Q90 180 90 130 Q90 80 150 80 Q210 80 210 140 Q210 180 260 180" fill="none" stroke="rgba(255,94,31,.18)" strokeWidth="1" strokeDasharray="3 4" />
        <path
          d="M40 180 Q90 180 90 130 Q90 80 150 80 Q210 80 210 140 Q210 180 260 180"
          fill="none"
          stroke="url(#snake-grad)"
          strokeWidth="14"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="6 4"
          style={{
            filter: "drop-shadow(0 0 10px rgba(255,94,31,.45))",
            animation: "hupper-snake-slither 2.4s linear infinite",
          }}
        />
        <path
          d="M40 180 Q90 180 90 130 Q90 80 150 80 Q210 80 210 140 Q210 180 260 180"
          fill="none"
          stroke="rgba(0,0,0,.35)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="2 12"
          style={{ animation: "hupper-snake-slither 2.4s linear infinite reverse" }}
        />
        <g transform="translate(260 180)">
          <path d="M-2 -10 Q14 -10 18 -3 Q22 0 18 3 Q14 10 -2 10 Q-10 10 -10 0 Q-10 -10 -2 -10 Z" fill="url(#snake-grad)" stroke="#0a0a0a" strokeWidth="1.2" />
          <circle cx="6" cy="-3" r="2.4" fill="#fff" />
          <circle cx="7" cy="-3" r="1.1" fill="#0a0a0a" />
          <path
            d="M18 0 L28 -2 M24 -1 L30 -4 M24 -1 L30 2"
            stroke="#ff5e1f"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
            style={{ transformOrigin: "0 0", animation: "hupper-snake-tongue 1.4s ease-in-out infinite" }}
          />
        </g>
        <g
          transform="translate(290 60)"
          style={{
            filter: "drop-shadow(0 0 8px #fff)",
            transformOrigin: "center",
            animation: "hupper-snake-fruit 1.2s ease-in-out infinite",
          }}
        >
          <circle r="9" fill="url(#fruit-grad)" />
          <path d="M0 -9 Q3 -14 7 -13" stroke="#16a34a" strokeWidth="2" fill="none" strokeLinecap="round" />
        </g>
      </svg>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/35" />
    </div>
  );
}

function StandardCard({ item }: { item: CardItem }) {
  const interactive = Boolean(item.onClick);

  const content = (
    <>
      <div className="relative min-h-[200px] border-b border-[var(--color-line)] bg-[linear-gradient(180deg,#0e0e0e_0%,#080808_100%)] overflow-hidden">
        <div className="absolute left-3.5 top-3.5 right-3.5 flex items-center justify-end z-[2]">
          <span
            className="w-6 h-6 rounded-md grid place-items-center text-sm"
            style={{
              border: "1px solid var(--color-line-emphasis)",
              background: "rgba(0,0,0,0.4)",
              color: "var(--color-text-secondary)",
            }}
          >
            +
          </span>
        </div>
        {item.art}
      </div>

      <div className="p-4">
        <h3 className="m-0 text-[20px] font-semibold tracking-[-0.01em] text-[var(--color-text-primary)]">
          {item.title}
        </h3>
        <p className="mt-1.5 mb-0 text-[13px] leading-[1.45] text-[var(--color-text-secondary)]">
          {item.desc}
        </p>
        <div className="mt-3.5 pt-3 grid grid-cols-1 gap-2 border-t border-[var(--color-line)]">
          {item.stats.map((stat) => (
            <div key={stat.label}>
              <div className="font-mono text-[9px] tracking-[0.16em] uppercase text-[var(--color-text-tertiary)]">{stat.label}</div>
              <div className="mt-1 font-mono text-[13px]" style={{ color: stat.muted ? "var(--color-text-tertiary)" : "var(--color-text-primary)" }}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  if (interactive) {
    return (
      <button
        type="button"
        onClick={item.onClick}
        className="hupper-game-card group relative flex flex-col rounded-[14px] overflow-hidden text-left transition-transform duration-200"
        style={{
          background: "var(--color-surface)",
          border: "1px solid var(--color-line)",
          cursor: "pointer",
        }}
      >
        <CardHoverOutline />
        {content}
      </button>
    );
  }

  return (
    <div
      className="hupper-game-card group relative flex flex-col rounded-[14px] overflow-hidden text-left transition-transform duration-200"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-line)",
        cursor: "default",
      }}
    >
      <CardHoverOutline />
      {content}
    </div>
  );
}

function TournamentCard({ onEnter }: { onEnter?: () => void }) {
  return (
    <article
      className="relative overflow-hidden rounded-[14px] lg:col-span-2"
      style={{
        background: "linear-gradient(135deg,#ff7a44 0%,#ff5e1f 55%,#e64a0f 100%)",
        color: "#1a0a02",
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(600px 300px at 90% 10%, rgba(255,255,255,.18), transparent 60%), radial-gradient(400px 200px at 0% 110%, rgba(0,0,0,.18), transparent 60%)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,.06) 1px,transparent 1px), linear-gradient(90deg,rgba(0,0,0,.06) 1px,transparent 1px)",
          backgroundSize: "36px 36px",
          maskImage: "radial-gradient(ellipse 70% 80% at 80% 30%, #000 30%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 80% at 80% 30%, #000 30%, transparent 80%)",
        }}
      />

      <div className="relative z-[1] p-9 flex flex-col gap-8 h-full">
        <div>
          <div className="font-mono text-[11px] tracking-[0.18em] uppercase inline-flex items-center gap-2 opacity-85">
            <span className="w-[7px] h-[7px] rounded-full bg-[#1a0a02] animate-pulse" />
            Weekly Tournaments
          </div>
          <h3 className="mt-4 mb-0 text-[42px] md:text-[48px] leading-[0.98] tracking-[-0.03em] font-bold max-w-[720px]">
            Compete weekly. Take it all home.
          </h3>
          <div className="mt-2.5 font-mono text-[14px] tracking-[0.04em] opacity-80">
            Tournaments every week · Single-elim · <strong>Winner takes 70%</strong>
          </div>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="flex flex-col gap-1 text-[#1a0a02]">
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase opacity-70">Prize pool up to</span>
            <span className="text-[54px] md:text-[56px] font-bold tracking-[-0.03em] leading-none">
              $10,000<sup className="text-[24px] font-semibold align-super opacity-80">+</sup>
            </span>
          </div>
          <button
            type="button"
            onClick={onEnter}
            className="rounded-full px-7 py-4 text-[15px] font-semibold inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5"
            style={{ background: "#1a0a02", color: "#ff7a44" }}
          >
            Enter tournament <span>→</span>
          </button>
        </div>
      </div>
    </article>
  );
}

export default function GamesGrid({
  onPlayPong,
  onOpenFPSModal,
  onPlayTowerDefense,
  onPlaySnake,
  onPlayMath,
  onOpenTournaments,
}: GamesGridProps) {
  const cards: CardItem[] = [
      {
        id: "fps",
        title: "FPS Arena",
        desc: "Precision duels, clean mechanics and fast rounds built for skill-based stakes.",
        onClick: onOpenFPSModal,
        art: null,
        stats: [
          { label: "Stake", value: "$1 — $100" },
        ],
      },
      {
        id: "tower-defense",
        title: "Tower Defense",
        desc: "Identical waves, two players. Outlast your opponent — last to lose all lives wins the pot.",
        onClick: onPlayTowerDefense,
        art: <TowerDefenseArt />,
        stats: [
          { label: "Stake", value: "$1 — $10" },
        ],
      },
      {
        id: "math",
        title: "Math",
        desc: "First to ten correct answers wins. No calculator, no edge.",
        onClick: onPlayMath,
        art: <MathArt />,
        stats: [
          { label: "Stake", value: "$1 — $10" },
        ],
      },
      {
        id: "pong",
        title: "Pong",
        desc: "Classic arcade. Best of five.",
        onClick: onPlayPong,
        art: <PongArt />,
        stats: [
          { label: "Stake", value: "$1 — $10" },
        ],
      },
      {
        id: "snake",
        title: "Snake",
        desc: "slither.io style. Hunt, grow, hold Q for 3.5s to cash out — die and your stake goes to the pot.",
        onClick: onPlaySnake,
        art: <SnakeArt />,
        stats: [
          { label: "Stake", value: "$1 — $100" },
        ],
      },
    ];

  const towerDefense = cards.find((card) => card.id === "tower-defense");
  const math = cards.find((card) => card.id === "math");
  const pong = cards.find((card) => card.id === "pong");
  const snake = cards.find((card) => card.id === "snake");
  const fps = cards.find((card) => card.id === "fps");

  return (
    <section id="games-grid" className="relative max-w-[1360px] mx-auto px-6 md:px-12 pt-16 pb-24 w-full">
      <GamesGridMotionStyles />
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-9">
        <div>
          <h2 className="text-[40px] md:text-[52px] lg:text-[64px] leading-[0.95] tracking-[-0.035em] font-display font-bold text-[var(--color-text-primary)]">
            Pick your <span className="text-[var(--color-brand)]">battlefield.</span>
          </h2>
        </div>
        <div className="flex flex-col gap-1.5 lg:items-end font-mono text-[11px] tracking-[0.12em] uppercase text-[var(--color-text-secondary)]">
          <span className="inline-flex items-center gap-2">
            <HeaderDot live />
            78 MATCHES IN PROGRESS
          </span>
          <span>$48,210 IN OPEN POTS</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-[18px] auto-rows-[minmax(260px,auto)]">
        {fps && <FpsArenaShowcase item={fps} />}

        {towerDefense && <StandardCard item={towerDefense} />}
        {math && <StandardCard item={math} />}
        <TournamentCard onEnter={onOpenTournaments} />
        {pong && <StandardCard item={pong} />}
        {snake && <StandardCard item={snake} />}
      </div>
    </section>
  );
}
